import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Aviso,
  Botao,
  Cartao,
  CartaoIndicador,
  EstadoVazio,
  Modal,
  Selo,
  Tabela,
  ValorFiscal,
  ValorPercentual,
  classesDeBotao,
  useDialogo,
  type ColunaTabela,
} from '../../componentes/ui';
import { BotaoExtrairXlsx } from '../../componentes/BotaoExtrairXlsx';
import { Link } from 'react-router-dom';
import { invalidarPedidos } from '../../componentes/PedidosDeFechamento';
import { useParticipacao } from '../../hooks/useParticipacao';
import { useEmpresaCorrente } from '../../hooks/useSessao';
import { requisicaoAutenticada } from '../../servicos/http';
import {
  assessoraEmAlgum,
  type PapelNoServico,
  pedirFechamento,
} from '../../servicos/participacao';
import { diaLocal, simOuNao, type ColunaDaPlanilha } from '../../servicos/planilha';
import { ErroDaApi } from '../ErroDaApi';
import { Pagina } from '../Carteira';
import { humanizar, rotuloDoStatus, tomDoStatus, type ContratoPassivo } from '../PassivoContratos';
import {
  COLUNAS_DA_PLANILHA as COLUNAS_DAS_PARCELAS,
  ModalDePagamento,
  tomDaParcela,
  type Parcela,
} from '../PassivoParcelas';

/**
 * ICMS Passivo de quem não é admin: os contratos em que a empresa escolhida é
 * parte — cliente, fornecedor do crédito ou assessor do cliente.
 *
 * ## O que cada papel vê
 *
 * O servidor decide (`GET /passivo/minhas/contratos`): o **cliente** e o
 * **assessor** dele veem a conta inteira — dívida, o que se paga, economia,
 * parcelas e o checklist; o **fornecedor** vê o crédito que vendeu, o deságio,
 * o que recebe e as parcelas. A dívida original e a economia são do cliente e
 * chegam como `null` para ele.
 *
 * ## O que cada papel faz
 *
 * Os dois passos do RN-241: o cliente registra o pagamento da parcela, com
 * comprovante; o assessor confere e confirma. O assessor também simula para os
 * clientes da carteira e pede o fechamento. Criar, ativar, cancelar e mexer no
 * checklist continuam sendo da operação.
 */

type PapelNoContrato = PapelNoServico;

interface ContratoDaParte {
  readonly id: string;
  readonly status: ContratoPassivo['status'];
  readonly clienteId: string;
  readonly clienteNome: string;
  readonly fornecedorId: string;
  readonly fornecedorNome: string;
  /** `null` para quem só é fornecedor. */
  readonly valorDividaOriginal: number | null;
  readonly economia: number | null;
  readonly percentualEconomia: number | null;
  readonly valorCreditoComprado: number;
  readonly percentualDesagio: number;
  readonly valorFinalPagar: number;
  readonly quantidadeParcelas: number;
  readonly taxaJurosMensal: number;
  readonly cronogramaGerado: boolean;
  readonly criadoEm: string;
  readonly papeis: readonly PapelNoContrato[];
  readonly fechamentoSolicitado: boolean;
}

interface ContratoDaParteDetalhado extends ContratoDaParte {
  readonly parcelas: readonly Parcela[];
  /** Do cliente e do assessor; `null` para o fornecedor. */
  readonly checklist: {
    readonly etapas: readonly {
      readonly id: string;
      readonly titulo: string;
      readonly concluida: boolean;
      readonly dataConclusao: string | null;
    }[];
    readonly percentualConcluido: number;
  } | null;
}

const CHAVE = ['passivo-da-parte'] as const;

const ROTULO_DO_PAPEL: Readonly<Record<PapelNoContrato, string>> = {
  CLIENTE: 'cliente',
  FORNECEDOR: 'fornecedor',
  ASSESSOR: 'assessor',
};

const papeisPorExtenso = (papeis: readonly PapelNoContrato[]): string =>
  papeis.map((p) => ROTULO_DO_PAPEL[p]).join(' e ');

const COLUNAS_DA_PLANILHA: readonly ColunaDaPlanilha<ContratoDaParte>[] = [
  { titulo: 'Cliente', largura: 36, valor: (c) => c.clienteNome },
  { titulo: 'Fornecedor do crédito', largura: 36, valor: (c) => c.fornecedorNome },
  { titulo: 'Papel da empresa', largura: 18, valor: (c) => papeisPorExtenso(c.papeis) },
  { titulo: 'Status', largura: 14, valor: (c) => rotuloDoStatus(c.status) },
  { titulo: 'Dívida original', formato: 'moeda', valor: (c) => c.valorDividaOriginal },
  { titulo: 'Crédito comprado', formato: 'moeda', valor: (c) => c.valorCreditoComprado },
  { titulo: 'Deságio', formato: 'percentual', valor: (c) => c.percentualDesagio },
  { titulo: 'Valor a pagar', formato: 'moeda', valor: (c) => c.valorFinalPagar },
  { titulo: 'Economia', formato: 'moeda', valor: (c) => c.economia },
  { titulo: 'Economia (%)', formato: 'percentual', valor: (c) => c.percentualEconomia },
  { titulo: 'Parcelas', largura: 10, valor: (c) => c.quantidadeParcelas },
  { titulo: 'Juros ao mês', formato: 'percentual', valor: (c) => c.taxaJurosMensal },
  { titulo: 'Cronograma gerado', largura: 12, valor: (c) => simOuNao(c.cronogramaGerado) },
  { titulo: 'Criado em', formato: 'data', valor: (c) => diaLocal(c.criadoEm) },
];

export function PassivoDaParte(): JSX.Element {
  const empresa = useEmpresaCorrente();
  const participacao = useParticipacao();
  const queryClient = useQueryClient();
  const { pedirTexto } = useDialogo();
  const [abertoId, setAbertoId] = useState<string | null>(null);
  // Quem assessora em qualquer serviço simula para os clientes aqui também.
  const assessor = assessoraEmAlgum(participacao.data);

  const contratos = useQuery({
    queryKey: [...CHAVE, empresa],
    queryFn: () =>
      requisicaoAutenticada<ContratoDaParte[]>(
        `/passivo/minhas/contratos?empresa=${encodeURIComponent(empresa ?? '')}`,
      ),
    enabled: empresa !== null,
  });

  /** O pedido sobre um contrato que a operação deixou em simulação. */
  const pedir = useMutation({
    mutationFn: (entrada: { readonly c: ContratoDaParte; readonly observacao: string }) =>
      pedirFechamento({
        empresa: empresa ?? '',
        servico: 'PASSIVO',
        clienteId: entrada.c.clienteId,
        contratoPassivoId: entrada.c.id,
        ...(entrada.observacao.trim() === '' ? {} : { observacao: entrada.observacao.trim() }),
      }),
    onSuccess: () => invalidarPedidos(queryClient),
  });

  const pedirPara = (c: ContratoDaParte): void => {
    void (async () => {
      const observacao = await pedirTexto({
        titulo: 'Pedir o fechamento deste contrato?',
        descricao:
          'A operação é avisada e segue com o contrato: confere os números com o fornecedor e ativa o cronograma.',
        rotulo: 'Observação para a operação (opcional)',
        multilinha: true,
        rotuloConfirmar: 'Pedir fechamento',
      });
      if (observacao !== null) pedir.mutate({ c, observacao });
    })();
  };

  const lista = contratos.data ?? [];
  const ativos = lista.filter((c) => c.status === 'ATIVO');
  const vivos = lista.filter((c) => c.status === 'ATIVO' || c.status === 'FINALIZADO');
  const veCliente = lista.some((c) => c.valorDividaOriginal !== null);

  const colunas: readonly ColunaTabela<ContratoDaParte>[] = [
    {
      chave: 'partes',
      titulo: 'Contrato',
      larguraMinima: '17rem',
      celula: (c) => (
        <span className="flex flex-col">
          <button
            type="button"
            onClick={() => setAbertoId(c.id)}
            className="text-left text-sm text-conteudo-primario underline-offset-4 hover:text-acento-ciano hover:underline"
          >
            {c.clienteNome}
          </button>
          <span className="text-xs text-conteudo-sutil">crédito de {c.fornecedorNome}</span>
          <span className="text-xs text-conteudo-secundario">
            Sua empresa: {papeisPorExtenso(c.papeis)}
          </span>
        </span>
      ),
    },
    {
      chave: 'status',
      titulo: 'Status',
      larguraMinima: '10rem',
      // O pedido de fechamento do assessor fica sob o status, como no Pontual.
      celula: (c) => (
        <span className="flex flex-col items-start gap-1">
          <Selo tom={tomDoStatus(c.status)}>{rotuloDoStatus(c.status)}</Selo>
          {c.papeis.includes('ASSESSOR') &&
            c.status === 'SIMULACAO' &&
            (c.fechamentoSolicitado ? (
              <Selo tom="atencao" tamanho="pequeno">
                Fechamento pedido
              </Selo>
            ) : (
              <Botao
                variante="secundario"
                tamanho="pequeno"
                carregando={pedir.isPending && pedir.variables?.c.id === c.id}
                onClick={() => pedirPara(c)}
              >
                Pedir fechamento
              </Botao>
            ))}
        </span>
      ),
    },
    ...(veCliente
      ? ([
          {
            chave: 'valorDividaOriginal',
            titulo: 'Dívida original',
            numerica: true,
            celula: (c) => <ValorFiscal valor={c.valorDividaOriginal} tamanho="pequeno" />,
          },
        ] satisfies ColunaTabela<ContratoDaParte>[])
      : []),
    {
      chave: 'valorCreditoComprado',
      titulo: 'Crédito comprado',
      numerica: true,
      celula: (c) => (
        <span className="flex flex-col items-end">
          <ValorFiscal valor={c.valorCreditoComprado} tamanho="pequeno" />
          <span className="text-xs text-conteudo-sutil">
            deságio <ValorPercentual percentual={c.percentualDesagio} tamanho="pequeno" />
          </span>
        </span>
      ),
    },
    {
      chave: 'valorFinalPagar',
      titulo: 'Valor a pagar',
      numerica: true,
      celula: (c) => <ValorFiscal valor={c.valorFinalPagar} tamanho="pequeno" />,
    },
    ...(veCliente
      ? ([
          {
            chave: 'economia',
            titulo: 'Economia',
            numerica: true,
            celula: (c) =>
              c.economia === null ? (
                <span className="text-conteudo-sutil">—</span>
              ) : (
                <span className="flex flex-col items-end">
                  <ValorFiscal valor={c.economia} variante="economia" tamanho="pequeno" />
                  <ValorPercentual percentual={c.percentualEconomia} tamanho="pequeno" />
                </span>
              ),
          },
        ] satisfies ColunaTabela<ContratoDaParte>[])
      : []),
    {
      chave: 'quantidadeParcelas',
      titulo: 'Parcelas',
      numerica: true,
      larguraMinima: '8rem',
      celula: (c) => (
        <span className="flex flex-col items-end">
          <span className="valor-fiscal text-sm">{c.quantidadeParcelas}</span>
          <span className="text-xs text-conteudo-sutil">
            <ValorPercentual percentual={c.taxaJurosMensal} tamanho="pequeno" /> a.m.
          </span>
        </span>
      ),
    },
  ];

  return (
    <Pagina
      titulo="Créditos ICMS Passivos"
      descricao="Os contratos de quitação de dívida de ICMS com crédito de terceiro em que esta empresa é parte — como cliente, fornecedor do crédito ou assessor do cliente."
      contagem={lista.length}
      acao={
        <div className="flex flex-wrap items-center gap-3">
          <BotaoExtrairXlsx
            titulo="Contratos - ICMS Passivo"
            disabled={contratos.data === undefined}
            colunas={COLUNAS_DA_PLANILHA}
            linhas={lista}
          />
        </div>
      }
    >
      {contratos.isError && <ErroDaApi titulo="Não foi possível carregar" erro={contratos.error} />}
      {pedir.isError && <ErroDaApi titulo="Não foi possível pedir" erro={pedir.error} />}

      <div className="grid gap-4 sm:grid-cols-3">
        <CartaoIndicador
          rotulo="Contratos ativos"
          valor={<ValorFiscal valor={ativos.length} moeda={false} casas={0} tamanho="grande" />}
          detalhe="Com cronograma de parcelas em andamento"
        />
        <CartaoIndicador
          rotulo="Valor contratado"
          valor={
            <ValorFiscal
              valor={vivos.reduce((t, c) => t + c.valorFinalPagar, 0)}
              tamanho="grande"
            />
          }
          detalhe="O que se paga pelo crédito, sem juros — contratos ativos e finalizados"
        />
        {veCliente ? (
          <CartaoIndicador
            rotulo="Economia"
            valor={
              <ValorFiscal
                valor={vivos.reduce((t, c) => t + (c.economia ?? 0), 0)}
                variante="economia"
                tamanho="grande"
              />
            }
            detalhe="Sobre a dívida original, nos contratos ativos e finalizados"
          />
        ) : (
          <CartaoIndicador
            rotulo="Crédito vendido"
            valor={
              <ValorFiscal
                valor={vivos.reduce((t, c) => t + c.valorCreditoComprado, 0)}
                tamanho="grande"
              />
            }
            detalhe="Valor de face do crédito nos contratos ativos e finalizados"
          />
        )}
      </div>

      {!contratos.isPending && lista.length === 0 ? (
        <EstadoVazio
          titulo="Nenhum contrato de Passivo"
          descricao={
            assessor
              ? 'Ainda não há contrato para os seus clientes. Simule em Simulações e, com o cliente de acordo, peça o fechamento: a operação monta o contrato com os seus números.'
              : 'Esta empresa ainda não é parte de nenhum contrato de Passivo.'
          }
          {...(assessor
            ? {
                acao: (
                  <Link to="/passivo" className={classesDeBotao('primario', 'medio')}>
                    Ir para Simulações
                  </Link>
                ),
              }
            : {})}
        />
      ) : (
        <Cartao como="section" corpoSemPreenchimento>
          <Tabela
            legenda="Contratos de ICMS Passivo da empresa"
            densidade="compacta"
            colunas={colunas}
            dados={lista}
            chaveDaLinha={(c) => c.id}
            chaveDeCabecalhoDaLinha="partes"
            carregando={contratos.isPending}
          />
        </Cartao>
      )}

      <DetalheDoContrato
        contratoId={abertoId}
        aoFechar={() => setAbertoId(null)}
        aoPedir={pedirPara}
        pedindo={pedir.isPending}
      />
    </Pagina>
  );
}

/* ─── Detalhe ─────────────────────────────────────────────────────────────── */

/**
 * Um contrato por dentro, na visão da parte: os números, o checklist (só
 * leitura) e as parcelas com os dois passos do RN-241 — o cliente registra o
 * pagamento, o assessor confirma.
 */
function DetalheDoContrato(props: {
  readonly contratoId: string | null;
  readonly aoFechar: () => void;
  readonly aoPedir: (c: ContratoDaParte) => void;
  readonly pedindo: boolean;
}): JSX.Element {
  const empresa = useEmpresaCorrente();
  const queryClient = useQueryClient();
  const [pagando, setPagando] = useState<Parcela | null>(null);
  const id = props.contratoId;

  const contrato = useQuery({
    queryKey: [...CHAVE, empresa, id],
    queryFn: () =>
      requisicaoAutenticada<ContratoDaParteDetalhado>(
        `/passivo/minhas/contratos/${id ?? ''}?empresa=${encodeURIComponent(empresa ?? '')}`,
      ),
    enabled: id !== null && empresa !== null,
  });
  const c = contrato.data;
  const cliente = c?.papeis.includes('CLIENTE') === true;
  const assessor = c?.papeis.includes('ASSESSOR') === true;

  const invalidar = (): void => {
    void queryClient.invalidateQueries({ queryKey: CHAVE });
  };

  const confirmar = useMutation({
    mutationFn: (parcelaId: string) =>
      requisicaoAutenticada<unknown>(
        `/passivo/contratos/${id ?? ''}/parcelas/${parcelaId}/confirmacao`,
        { metodo: 'POST', corpo: {} },
      ),
    onSuccess: invalidar,
  });

  const colunas: readonly ColunaTabela<Parcela>[] = [
    {
      chave: 'numero',
      titulo: '#',
      numerica: true,
      larguraMinima: '3.5rem',
      celula: (p) => <span className="valor-fiscal text-sm">{p.numero}</span>,
    },
    {
      chave: 'dataVencimento',
      titulo: 'Vencimento',
      larguraMinima: '8rem',
      celula: (p) => (
        <span className="valor-fiscal whitespace-nowrap text-sm">
          {new Date(p.dataVencimento).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
        </span>
      ),
    },
    {
      chave: 'valorParcela',
      titulo: 'Parcela',
      numerica: true,
      celula: (p) => <ValorFiscal valor={p.valorParcela} />,
    },
    {
      chave: 'juros',
      titulo: 'Juros',
      numerica: true,
      celula: (p) => <ValorFiscal valor={p.juros} tamanho="pequeno" />,
    },
    {
      chave: 'saldoDevedor',
      titulo: 'Saldo devedor',
      numerica: true,
      celula: (p) => <ValorFiscal valor={p.saldoDevedor} tamanho="pequeno" />,
    },
    {
      chave: 'status',
      titulo: 'Situação',
      larguraMinima: '12rem',
      // Pagamento e conferência dentro da situação: são os dois passos do
      // RN-241, e em colunas próprias o botão do cliente ficava fora da tela.
      celula: (p) => (
        <span className="flex flex-col items-start gap-1">
          <Selo tom={tomDaParcela(p)}>{humanizar(p.status)}</Selo>
          {p.diasDeAtraso > 0 && (
            <span className="text-xs text-erro">{p.diasDeAtraso} dia(s) de atraso</span>
          )}
          {p.dataPagamento === null ? (
            cliente &&
            p.status !== 'CANCELADA' && (
              <Botao variante="secundario" tamanho="pequeno" onClick={() => setPagando(p)}>
                Registrar pagamento
              </Botao>
            )
          ) : (
            <>
              <span className="text-xs text-conteudo-sutil">
                paga em {new Date(p.dataPagamento).toLocaleDateString('pt-BR')}
              </span>
              {p.confirmadoPorAssessor ? (
                <span className="text-xs text-economia">Conferida pelo assessor</span>
              ) : assessor ? (
                <Botao
                  variante="secundario"
                  tamanho="pequeno"
                  carregando={confirmar.isPending && confirmar.variables === p.id}
                  onClick={() => confirmar.mutate(p.id)}
                >
                  Confirmar pagamento
                </Botao>
              ) : (
                <span className="text-xs text-conteudo-sutil">Aguardando conferência</span>
              )}
            </>
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <Modal
        aberto={id !== null}
        aoFechar={props.aoFechar}
        titulo="Contrato de Passivo"
        descricao={
          c === undefined
            ? undefined
            : `${c.clienteNome} · crédito de ${c.fornecedorNome} · sua empresa: ${papeisPorExtenso(c.papeis)}`
        }
        largura="larga"
        rodape={<Botao onClick={props.aoFechar}>Fechar</Botao>}
      >
        {contrato.isError && <ErroDaApi titulo="Não foi possível carregar" erro={contrato.error} />}
        {c === undefined ? (
          !contrato.isError && (
            <p className="py-8 text-center text-sm text-conteudo-sutil">Carregando…</p>
          )
        ) : (
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center gap-3">
              <Selo tom={tomDoStatus(c.status)}>{rotuloDoStatus(c.status)}</Selo>
              {c.status === 'SIMULACAO' && (
                <span className="text-sm text-conteudo-secundario">
                  Em simulação: as parcelas saem quando a operação ativar o contrato.
                </span>
              )}
            </div>

            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
              {c.valorDividaOriginal !== null && (
                <Item rotulo="Dívida original">
                  <ValorFiscal valor={c.valorDividaOriginal} tamanho="pequeno" />
                </Item>
              )}
              <Item rotulo="Crédito comprado">
                <span className="flex flex-col">
                  <ValorFiscal valor={c.valorCreditoComprado} tamanho="pequeno" />
                  <span className="text-xs text-conteudo-sutil">
                    deságio <ValorPercentual percentual={c.percentualDesagio} tamanho="pequeno" />
                  </span>
                </span>
              </Item>
              <Item rotulo="Valor a pagar">
                <ValorFiscal valor={c.valorFinalPagar} tamanho="pequeno" />
              </Item>
              {c.economia !== null && (
                <Item rotulo="Economia">
                  <span className="flex flex-col">
                    <ValorFiscal valor={c.economia} variante="economia" tamanho="pequeno" />
                    <ValorPercentual percentual={c.percentualEconomia} tamanho="pequeno" />
                  </span>
                </Item>
              )}
              <Item rotulo="Prazo">
                <span className="text-sm text-conteudo-secundario">
                  {c.quantidadeParcelas} parcelas,{' '}
                  <ValorPercentual percentual={c.taxaJurosMensal} tamanho="pequeno" /> a.m.
                </span>
              </Item>
            </dl>

            {assessor && c.status === 'SIMULACAO' && (
              <section className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-borda p-4">
                <p className="text-sm text-conteudo-secundario">
                  {c.fechamentoSolicitado
                    ? 'Fechamento pedido — a operação já foi avisada.'
                    : 'Com o cliente de acordo, peça o fechamento à operação.'}
                </p>
                {!c.fechamentoSolicitado && (
                  <Botao carregando={props.pedindo} onClick={() => props.aoPedir(c)}>
                    Pedir fechamento
                  </Botao>
                )}
              </section>
            )}

            {c.checklist !== null && c.checklist.etapas.length > 0 && (
              <section className="flex flex-col gap-2">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="text-sm font-semibold text-conteudo-primario">
                    Andamento da operação
                  </h3>
                  <span className="text-xs text-conteudo-sutil">
                    {c.checklist.percentualConcluido}% concluído
                  </span>
                </div>
                <ul className="flex flex-col gap-1">
                  {c.checklist.etapas.map((e) => (
                    <li
                      key={e.id}
                      className="flex items-start gap-3 rounded-card border border-borda px-3 py-2"
                    >
                      <span
                        aria-hidden="true"
                        className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border text-[0.6rem] ${
                          e.concluida
                            ? 'border-economia bg-economia/15 text-economia'
                            : 'border-borda text-transparent'
                        }`}
                      >
                        ✓
                      </span>
                      <span className="flex flex-col">
                        <span className="text-sm text-conteudo-primario">
                          {e.titulo}
                          <span className="sr-only">
                            {e.concluida ? ' — concluída' : ' — pendente'}
                          </span>
                        </span>
                        {e.dataConclusao !== null && (
                          <span className="text-xs text-conteudo-sutil">
                            Concluída em {new Date(e.dataConclusao).toLocaleDateString('pt-BR')}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-conteudo-primario">
                  Parcelas ({c.parcelas.length})
                </h3>
                {c.parcelas.length > 0 && (
                  <BotaoExtrairXlsx
                    titulo="Parcelas do contrato - Passivo"
                    tamanho="pequeno"
                    colunas={COLUNAS_DAS_PARCELAS}
                    linhas={[...c.parcelas]}
                  />
                )}
              </div>
              {cliente && c.parcelas.some((p) => p.dataPagamento === null) && (
                <Aviso tom="informacao">
                  Pagou uma parcela? Registre o pagamento com o comprovante — o assessor confere e
                  confirma.
                </Aviso>
              )}
              {confirmar.isError && (
                <ErroDaApi titulo="Não foi possível confirmar" erro={confirmar.error} />
              )}
              {c.parcelas.length === 0 ? (
                <p className="text-sm text-conteudo-sutil">
                  {c.status === 'SIMULACAO'
                    ? 'O cronograma é gerado quando o contrato é ativado.'
                    : 'Nenhuma parcela gerada.'}
                </p>
              ) : (
                <Tabela
                  legenda="Parcelas do contrato"
                  densidade="compacta"
                  colunas={colunas}
                  dados={[...c.parcelas]}
                  chaveDaLinha={(p) => p.id}
                  chaveDeCabecalhoDaLinha="numero"
                />
              )}
            </section>
          </div>
        )}
      </Modal>

      <ModalDePagamento
        contratoId={id ?? ''}
        parcela={pagando}
        aoFechar={() => setPagando(null)}
        aoPagar={invalidar}
      />
    </>
  );
}

function Item(props: { readonly rotulo: string; readonly children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-conteudo-sutil">{props.rotulo}</dt>
      <dd>{props.children}</dd>
    </div>
  );
}
